/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Typography, breakpoints } from '@coursera/cds-core';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import ArticleBreadcrumbs from 'bundles/shared-articles/components/ArticleBreadcrumbs';
import type { BreadcrumbLink } from 'bundles/shared-articles/components/ArticleBreadcrumbs';
import ArticleByLine from 'bundles/shared-articles/components/ArticleByLine';
import ArticleFeaturedImage from 'bundles/shared-articles/components/ArticleFeaturedImage';
import isExternalLink from 'bundles/shared-articles/utils/isExternalLink';
import type { Asset } from 'bundles/unified-seo-content-common/__generated__/baseContentfulTypes';

export type ArticleHeaderProps = {
  title: string;
  subtitle?: string;
  featuredImage?: Partial<Asset> | null;
  featuredImageCaption?: string | null;
  featuredImageLink?: string | null;
  latestPublishedDate: string;
  authorNames: string;
  breadcrumbLinks: BreadcrumbLink[];
};

const styles = {
  contentContainer: css`
    position: relative;
  `,
  title: css`
    margin-bottom: 1rem;
    position: relative;
  `,
  headerByLine: css`
    margin-bottom: 1rem;
    ${breakpoints.down('sm')} {
      max-width: 100%;
    }
  `,
  subtitle: css`
    font-size: 1.125rem;
    line-height: 1.875rem;
    color: #002d72;
    margin-bottom: 1.5rem;
  `,
};

/**
 * Renders the header of an article which includes the title, subtitle, author names, and featured image.
 * @param {Object} props - The properties for the article header component.
 * @param {string} props.title - The title of the article.
 * @param {string} props.subtitle - The subtitle of the article.
 * @param {string[]} props.authorNames - The names of the authors.
 * @param {Object} props.featuredImage - The featured image properties.
 * @param {string} props.featuredImageCaption - The caption of the featured image.
 * @param {string} props.featuredImageLink - The link of the featured image.
 * @param {string} props.latestPublishedDate - The latest published date of the article.
 * @param {Object[]} props.breadcrumbLinks - The breadcrumb links for the article.
 * @returns {React.Element} The rendered article header component.
 */
const ArticleHeader = ({
  title,
  subtitle,
  authorNames,
  featuredImage,
  featuredImageCaption,
  featuredImageLink,
  latestPublishedDate,
  breadcrumbLinks,
}: ArticleHeaderProps) => {
  const articleFeaturedImg = (
    <ArticleFeaturedImage featuredImage={featuredImage} featuredImageCaption={featuredImageCaption} />
  );

  return (
    <div className="rc-ArticleHeader">
      <div css={styles.contentContainer}>
        <ArticleBreadcrumbs title={title} links={breadcrumbLinks} />
        <Typography component="h1" variant="d2semibold" css={styles.title} data-e2e="article-page-title">
          {title}
        </Typography>
        <div css={styles.headerByLine}>
          <ArticleByLine date={latestPublishedDate} title={title} authorNames={authorNames} />
        </div>
        {subtitle && (
          <Typography variant="h2" color="highlightBlue" component="p" css={styles.subtitle}>
            {subtitle}
          </Typography>
        )}
      </div>
      {featuredImage && featuredImage.url && featuredImageLink ? (
        <TrackedA
          href={featuredImageLink}
          target={isExternalLink(featuredImageLink) ? '_blank' : '_self'}
          rel={isExternalLink(featuredImageLink) ? 'noopener noreferrer' : ''}
          trackingName="article_featured_image_link"
          data={{ articleTitle: title, featuredImageTitle: featuredImage.title }}
        >
          {articleFeaturedImg}
        </TrackedA>
      ) : (
        articleFeaturedImg
      )}
    </div>
  );
};

export default ArticleHeader;
